import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useLang } from "../../lib/LangContext";
import {
  DEFAULT_CONSENT, OPEN_CONSENT_EVENT, loadConsentedScripts, readConsent, writeConsent,
} from "../../lib/consent";

const CATEGORIES = [
  ["necessary", "Necessary", "Required for login, security and remembering your cookie choice. Always on."],
  ["preferences", "Preferences", "Remembers choices such as your interface language."],
  ["analytics", "Analytics", "Helps us understand how the website is used. Off until you allow it."],
  ["marketing", "Marketing", "Used to measure and improve campaigns. Off until you allow it."],
];

export default function CookieConsent() {
  const { tr } = useLang();
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const saved = readConsent();
    if (saved) {
      window.postMintConsent = saved;
      loadConsentedScripts(saved);
    } else {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    const reopen = () => {
      setPrefs(readConsent() || DEFAULT_CONSENT);
      setManage(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, reopen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, reopen);
  }, []);

  const save = useCallback((choice) => {
    loadConsentedScripts(writeConsent(choice));
    setManage(false);
    setVisible(false);
  }, []);

  if (!visible) return null;

  const equalButton =
    "rounded-full px-5 py-2.5 text-sm font-semibold transition-colors sm:flex-1 border";

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-modal="false"
      aria-label={tr("Cookie preferences")}
      className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-5 soft-shadow-lg sm:p-6"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FFF3EF] text-[#F95738]">
          <Cookie className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="flex-1">
          <h2 className="font-display text-base font-semibold text-slate-900">{tr("Your cookie choice")}</h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">
            {tr("We use necessary cookies to run this website and keep you signed in. Preference, analytics and marketing cookies are only used if you allow them — nothing non-essential loads before you choose.")}{" "}
            <Link
              to="/legal/cookie-policy"
              data-testid="cookie-policy-link"
              className="font-semibold text-[#F95738] hover:underline"
            >
              {tr("Cookie Policy")}
            </Link>
          </p>

          {manage && (
            <div className="mt-4 space-y-3" data-testid="cookie-preferences-panel">
              {CATEGORIES.map(([key, label, desc]) => (
                <label
                  key={key}
                  className="flex items-start gap-3 rounded-2xl border border-slate-200 p-3"
                  htmlFor={`cookie-${key}`}
                >
                  <input
                    id={`cookie-${key}`}
                    data-testid={`cookie-toggle-${key}`}
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-[#F95738]"
                    disabled={key === "necessary"}
                    checked={key === "necessary" ? true : Boolean(prefs[key])}
                    onChange={(e) => setPrefs((p) => ({ ...p, [key]: e.target.checked }))}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">{tr(label)}</span>
                    <span className="block text-xs text-slate-500">{tr(desc)}</span>
                  </span>
                </label>
              ))}
            </div>
          )}

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              data-testid="cookie-accept-all"
              onClick={() => save({ preferences: true, analytics: true, marketing: true })}
              className={`${equalButton} border-slate-900 bg-white text-slate-900 hover:bg-slate-100`}
            >
              {tr("Accept all")}
            </button>
            <button
              type="button"
              data-testid="cookie-reject-non-essential"
              onClick={() => save({ preferences: false, analytics: false, marketing: false })}
              className={`${equalButton} border-slate-900 bg-white text-slate-900 hover:bg-slate-100`}
            >
              {tr("Reject non-essential")}
            </button>
            {manage ? (
              <button
                type="button"
                data-testid="cookie-save-preferences"
                onClick={() => save(prefs)}
                className={`${equalButton} border-slate-900 bg-slate-900 text-white hover:bg-slate-800`}
              >
                {tr("Save preferences")}
              </button>
            ) : (
              <button
                type="button"
                data-testid="cookie-manage-preferences"
                onClick={() => setManage(true)}
                className={`${equalButton} border-slate-300 bg-white text-slate-700 hover:bg-slate-100`}
              >
                {tr("Manage preferences")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
